import React, { useState, useEffect } from "react";
import { IoCloseSharp } from "react-icons/io5";
import { FaRegBuilding } from "react-icons/fa";
import Button from "../../../components/ui/Button";
import FileUpload from "../../../components/ui/FileUpload";
import Input from "../../../components/ui/Input";
import { strings } from "../../../strings/strings";
import { apiCall } from "../../../api/fetchClient";
import { ADD_TRUSTED_BY_URL, UPDATE_TRUSTED_BY_URL } from "../../../api/url";

const BrandModal = ({ isOpen, onClose, onSave, brand }) => {
  const [formData, setFormData] = useState({
    name: "",
    logo: null
  });


  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (brand) {
        const initial = {
          name: brand.name || "",
          logo: brand.logo || null
        };
        setFormData(initial);
      } else {
        const initial = {
          name: "",
          logo: null
        };
        setFormData(initial);
      }
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen, brand]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Brand name is required";
    if (!brand && !formData.logo) newErrors.logo = "Brand logo is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setIsSubmitting(true);
      const dataToSend = new FormData();

      // Always send the name so the payload has at least one key
      if (formData.name) {
        dataToSend.append("name", formData.name);
      }

      // Only append the logo if it's a new file (not the existing URL)
      if (formData.logo instanceof Blob || formData.logo instanceof File) {
        dataToSend.append("logo", formData.logo, formData.logo.name || 'logo.png');
      }

      let response;
      if (brand) {
        response = await apiCall(`${UPDATE_TRUSTED_BY_URL}${brand._id || brand.id}`, 'PUT', dataToSend, true);
      } else {
        response = await apiCall(ADD_TRUSTED_BY_URL, 'POST', dataToSend, true);
      }

      onSave(response?.results || formData);
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity" onClick={onClose} />
      
      <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col border border-gray-100 dark:border-slate-800">
        
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-gray-100 dark:border-slate-800 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <FaRegBuilding className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {brand ? strings.EDIT_BRAND : strings.ADD_BRAND}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {brand ? "Update the details for this brand" : "Enter details to create a new brand"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-2 hover:bg-gray-50 dark:hover:bg-slate-800 rounded-lg"
          >
            <IoCloseSharp className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-5 sm:p-6 space-y-6" id="brand-form" noValidate>
          
          <div className="space-y-5">
            <Input
              id="name"
              name="name"
              label={strings.BRAND_NAME}
              value={formData.name}
              onChange={handleChange}
              placeholder={strings.BRAND_NAME_PLACEHOLDER}
              error={errors.name}
              required
            />
            
            <div>
              <FileUpload
                label={strings.BRAND_LOGO}
                name="logo"
                value={formData.logo}
                onChange={handleChange}
                error={errors.logo}
              />
            </div>
          </div>
          
        </form>

        <div className="flex items-center justify-end gap-3 p-5 sm:px-6 border-t border-gray-100 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-900 shrink-0">
          <Button type="button" variant="secondary" onClick={onClose} className="px-6">
            {strings.CANCEL}
          </Button>
          <Button type="submit" form="brand-form" variant="primary" className="px-6" disabled={isSubmitting}>
            {isSubmitting ? (brand ? "Updating..." : "Adding...") : (brand ? strings.SAVE_CHANGES : strings.ADD_BRAND)}
          </Button>
        </div>

      </div>
    </div>
  );
};

export default BrandModal;
